<script>
import { required } from "vuelidate/lib/validators";
import validationMessages from "@/components/validations";
import vue2Dropzone from "vue2-dropzone";
import "vue2-dropzone/dist/vue2Dropzone.min.css";
import Games from "@/services/Games";

export default {
  components: {
    validationMessages,
    vueDropzone: vue2Dropzone,
  },
  data() {
    return {
      csrf_token: localStorage.getItem("csrf_token"),
      category_id: null,
      name: "",
      active: 1,
      file: null,
      submitted: false,
      tryingToEdit: false,
      showModal: false,
      categories: [],
      dropzoneOptions: {
        url: "/",
        autoProcessQueue: false,
        addRemoveLinks: false,
        thumbnailWidth: 150,
        thumbnailMethod: "contain",
        maxFilesize: 10,
        maxFiles: 1,
        acceptedFiles:
          "image/jpeg,image/png,image/gif,image/bmp,image/webp,application/pdf",
        dictMaxFilesExceeded: "You can only upload one file",
        dictFileTooBig: "File is larger than 10MB",
        uploadMultiple: false,
      },
    };
  },
  validations: {
    category_id: { required },
    name: { required },
    file: { required },
  },
  methods: {
    async loadCategories() {
      try {
        const res = await Games.getDownloadCategories();
        const list = res.data && res.data.data ? res.data.data : [];
        this.categories = list.map((c) => ({
          value: c.id,
          text: c.name,
        }));
      } catch (e) {
        this.categories = [];
      }
    },
    selectFile(files) {
      setTimeout(() => {
        const queuedFiles =
          this.$refs.downloadDropzone &&
          this.$refs.downloadDropzone.getQueuedFiles
            ? this.$refs.downloadDropzone.getQueuedFiles()
            : [];
        if (queuedFiles.length > 1) {
          files.forEach((file) => {
            this.$refs.downloadDropzone.removeFile(file);
          });
        }
        this.file = queuedFiles[0] || null;
      }, 250);
    },
    async addDownload() {
      this.tryingToEdit = true;
      this.submitted = true;
      this.$v.$touch();

      if (this.$v.$invalid || !this.file) {
        this.tryingToEdit = false;
        return;
      }

      try {
        const formData = new FormData();
        formData.append("category_id", this.category_id);
        formData.append("name", this.name);
        formData.append("active", this.active);
        formData.append("file", this.file);
        formData.append("csrf_token", this.csrf_token);

        await Games.createDownload(formData);
        this.successmsg("Download has been created");
        this.closeModal();
        this.refreshData();
      } catch (error) {
        this.error =
          (error.response &&
            error.response.data &&
            error.response.data.error) ||
          "Failed to create download";
        this.failedmsg(this.error);
      } finally {
        this.tryingToEdit = false;
      }
    },
    refreshData() {
      this.$emit("onRefresh");
    },
    closeModal() {
      this.showModal = false;
      this.resetProps();
    },
    resetProps() {
      this.category_id = null;
      this.name = "";
      this.active = 1;
      this.file = null;
      this.submitted = false;
      if (
        this.$refs.downloadDropzone &&
        this.$refs.downloadDropzone.removeAllFiles
      ) {
        this.$refs.downloadDropzone.removeAllFiles(true);
      }
    },
  },
};
</script>

<template>
  <b-modal
    v-model="showModal"
    id="add_download"
    hide-header
    hide-footer
    body-class="p-0"
    size="lg"
    centered
    @shown="loadCategories"
    @hidden="resetProps"
  >
    <div class="download-modal">
      <header class="download-modal__hero">
        <h2>Add Download</h2>
        <p>Upload a new download asset for your games section.</p>
      </header>

      <b-form @submit.prevent="addDownload">
        <!-- DETAILS CARD -->
        <section class="download-card">
          <header class="download-card__header">
            <h3>Download Details</h3>
            <p>Set basic information for this download.</p>
          </header>

          <b-form-group label="Category *" label-for="download-category-id">
            <b-form-select
              id="download-category-id"
              v-model.number="category_id"
              :options="categories"
              :class="{ 'is-invalid': submitted && $v.category_id.$error }"
              required
            />
            <validationMessages
              v-if="submitted"
              :fieldName="'Category'"
              :validationName="$v.category_id"
            />
          </b-form-group>

          <b-form-group label="Name *" label-for="download-name">
            <b-form-input
              id="download-name"
              v-model.trim="name"
              :class="{ 'is-invalid': submitted && $v.name.$error }"
              required
            />
            <validationMessages
              v-if="submitted"
              :fieldName="'Name'"
              :validationName="$v.name"
            />
          </b-form-group>

          <b-form-group label="Active" label-for="download-active">
            <b-form-select
              id="download-active"
              v-model.number="active"
              :options="[
                { value: 1, text: 'Yes' },
                { value: 0, text: 'No' },
              ]"
            />
          </b-form-group>
        </section>

        <!-- FILE UPLOAD CARD -->
        <section class="download-card">
          <header class="download-card__header">
            <h3>Download File</h3>
            <p>Upload the image or file that users will download.</p>
          </header>

          <div
            class="download-upload"
            :class="{ 'has-error': submitted && $v.file.$error }"
          >
            <vue-dropzone
              ref="downloadDropzone"
              id="download-dropzone"
              :use-custom-slot="true"
              :options="dropzoneOptions"
              @vdropzone-files-added="selectFile"
              no-auto-process
              no-progress-bar
            >
              <div class="download-upload__placeholder">
                <i class="uil uil-import"></i>
                <span>Drag and drop a file, or click to browse</span>
                <small>Images (PNG, JPG, GIF, WEBP) or PDF up to 10MB</small>
              </div>
            </vue-dropzone>
          </div>

          <validationMessages
            v-if="submitted"
            :fieldName="'File'"
            :validationName="$v.file"
          />
        </section>

        <!-- FOOTER -->
        <footer class="download-modal__footer">
          <b-button variant="outline-secondary" @click="closeModal">
            Cancel
          </b-button>

          <b-button variant="primary" type="submit" :disabled="tryingToEdit">
            <b-spinner v-if="tryingToEdit" small class="me-2" />
            Save Download
          </b-button>
        </footer>

        <input type="hidden" name="csrf_token" v-model="csrf_token" />
      </b-form>
    </div>
  </b-modal>
</template>

<style scoped>
.download-modal {
  padding: 32px;
  background: #f7f8fb;
}

.download-modal__hero {
  margin-bottom: 24px;
}

.download-modal__hero h2 {
  font-weight: 600;
  font-size: 24px;
  margin-bottom: 6px;
  color: #151a2d;
}

.download-modal__hero p {
  margin: 0;
  color: #5f6b7d;
  font-size: 14px;
}

.download-card {
  background: #ffffff;
  border-radius: 16px;
  padding: 24px;
  margin-bottom: 20px;
  box-shadow: 0 10px 28px rgba(15, 23, 42, 0.06);
}

.download-card__header h3 {
  margin-bottom: 4px;
  font-size: 18px;
  font-weight: 600;
  color: #151a2d;
}

.download-card__header p {
  margin-bottom: 18px;
  color: #5f6b7d;
  font-size: 14px;
}

.download-upload {
  border: 2px dashed #d3d8e6;
  border-radius: 16px;
  background: #f7f8fb;
  padding: 28px;
  transition: border-color 0.2s ease, background 0.2s ease;
}

.download-upload.has-error {
  border-color: #f97373;
  background: #fff5f5;
}

.download-upload__placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  color: #5f6b7d;
  font-size: 14px;
}

.download-upload__placeholder i {
  font-size: 32px;
  color: #151a2d;
}

.download-upload__placeholder small {
  color: #a0aec0;
}

.download-modal__footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 12px;
}

@media (max-width: 767.98px) {
  .download-modal {
    padding: 20px;
  }

  .download-card {
    padding: 20px;
  }
}
</style>